import React from "react";
import "./Avatar.css";
import { clsx } from "../../utils/clsx";

// Breakpoints for responsive size objects
const BP = { xs: 480, sm: 640, md: 768, lg: 1024, xl: 99999 };

const getInitialVw = () => (typeof window !== 'undefined' ? window.innerWidth : 1024);

const useResponsiveViewport = (size) => {
  const [vw, setVw] = React.useState(getInitialVw);
  React.useEffect(() => {
    if (size && typeof size === 'object') {
      const handleResize = () => setVw(window.innerWidth);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
    return undefined;
  }, [size]);
  return vw;
};

const normalizeSizeToken = (value) => {
  if (!value) return value;
  const lowered = String(value).trim();
  const numericMatch = lowered.match(/^([0-9]+(?:\.[0-9]+)?)(px)?$/);
  if (numericMatch) return parseFloat(numericMatch[1]);
  const legacy = { large: 'lg', small: 'sm', default: 'md', middle: 'md' };
  return legacy[lowered] || lowered;
};

const resolveSizeValue = (size, vw) => {
  if (typeof size === 'number') return size;
  if (typeof size === 'string') return normalizeSizeToken(size);
  if (size && typeof size === 'object') {
    const pick = () => {
      if (vw < BP.xs) return size.xs ?? size.sm ?? size.md ?? size.lg ?? size.xl;
      if (vw < BP.sm) return size.sm ?? size.md ?? size.lg ?? size.xl ?? size.xs;
      if (vw < BP.md) return size.md ?? size.lg ?? size.xl ?? size.sm ?? size.xs;
      if (vw < BP.lg) return size.lg ?? size.xl ?? size.md ?? size.sm ?? size.xs;
      return size.xl ?? size.lg ?? size.md ?? size.sm ?? size.xs;
    };
    return resolveSizeValue(pick(), vw);
  }
  return 'md';
};

const resolveCounterDimension = (size) => {
  if (typeof size === 'number') return { numeric: size };
  if (typeof size === 'string') {
    const normalized = normalizeSizeToken(size);
    if (typeof normalized === 'number') return { numeric: normalized };
    return { token: normalized };
  }
  return {};
};

const resolveNumericInitialsFontSize = (size) =>
  `${Math.max(10, Math.min(32, Math.round(size * 0.28)))}px`;

// Broken signed asset URLs are immutable. Remember them for this page session
// so repeated avatar mounts use initials instead of generating repeated 404s.
const failedImageSources = new Set();

const hasFailedImageSource = (src) =>
  typeof src === 'string' && src.length > 0 && failedImageSources.has(src);

const AvatarComponent = ({
  src,
  srcSet,
  alt,
  size = "md", // number | 'large'|'small'|'default' | token ('xs','sm','md','lg','xl') | { xs, sm, md, lg, xl }
  shape = "circle", // 'circle' | 'square'
  initials,
  icon,
  gap = 4,
  draggable = true,
  crossOrigin,
  onError,
  children,
  className = "",
  style,
  ...rest
}) => {
  const vw = useResponsiveViewport(size);
  const resolvedSize = React.useMemo(() => resolveSizeValue(size, vw), [size, vw]);

  const sizeClass = typeof resolvedSize === 'string' ? `ds-avatar--${resolvedSize}` : '';
  const sizeStyle =
    typeof resolvedSize === 'number'
      ? {
          inlineSize: resolvedSize,
          blockSize: resolvedSize,
          width: resolvedSize,
          height: resolvedSize,
          '--_avatar-font-size': resolveNumericInitialsFontSize(resolvedSize),
        }
      : {};

  const [errored, setErrored] = React.useState(() => hasFailedImageSource(src));
  React.useEffect(() => {
    setErrored(hasFailedImageSource(src));
  }, [src, srcSet]);

  const handleImgError = React.useCallback(() => {
    let prevent = false;
    try {
      if (typeof onError === 'function') prevent = onError() === false;
    } catch (error) {
      // no-op: swallow consumer errors while still marking as errored when needed
    }
    if (!prevent) {
      if (typeof src === 'string' && src.length > 0) {
        failedImageSources.add(src);
      }
      setErrored(true);
    }
  }, [onError, src]);

  const DefaultUserIcon = () => (
    <svg
      viewBox="0 0 24 24"
      width="60%"
      height="60%"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 21a8 8 0 0 0-16 0" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );

  const content = React.useMemo(() => {
    const hasImgNode = React.isValidElement(src);
    const showImg = (typeof src === 'string' || hasImgNode) && !errored;
    if (showImg) {
      if (hasImgNode) return src;
      return (
        <img
          src={src}
          srcSet={srcSet}
          alt={alt || ''}
          draggable={draggable}
          crossOrigin={crossOrigin}
          onError={handleImgError}
        />
      );
    }

    if (initials) return <span style={{ paddingInline: gap }}>{initials}</span>;
    if (icon) return icon;
    if (children) return <span style={{ paddingInline: gap }}>{children}</span>;
    if (errored) return <DefaultUserIcon />;
    return <span style={{ paddingInline: gap }}>•</span>;
  }, [src, srcSet, alt, draggable, crossOrigin, errored, icon, children, initials, gap, handleImgError]);

  return (
    <span
      className={clsx(
        'ds-avatar',
        sizeClass,
        shape === 'square' && 'ds-avatar--square',
        className,
      )}
      style={{ ...sizeStyle, ...style }}
      {...rest}
    >
      {content}
    </span>
  );
};

const AvatarGroup = ({
  children,
  max,
  size = 'md',
  shape = 'circle',
  overlap = 8,
  className = '',
  style,
  counterLabel,
  ...rest
}) => {
  const items = React.Children.toArray(children).filter(Boolean);
  const vw = useResponsiveViewport(size);
  const resolvedSize = React.useMemo(() => resolveSizeValue(size, vw), [size, vw]);

  const limit = React.useMemo(() => {
    if (max == null) return items.length;
    if (typeof max === 'number') return max;
    if (typeof max === 'object') {
      const count = max.count ?? max.maxCount;
      return typeof count === 'number' ? count : items.length;
    }
    return items.length;
  }, [items.length, max]);

  const displayCount = Math.max(0, Math.min(limit, items.length));
  const shown = items.slice(0, displayCount);
  const extra = items.length - shown.length;

  const counterDims = resolveCounterDimension(resolvedSize);
  const numericSize = counterDims.numeric ?? (typeof resolvedSize === 'number' ? resolvedSize : undefined);
  const tokenSize = counterDims.token ?? (typeof resolvedSize === 'string' ? resolvedSize : undefined);
  const counterFontSize = numericSize ? Math.max(10, Math.round(numericSize * 0.35)) : 12;

  const counterStyle = {
    ...(numericSize
      ? { inlineSize: numericSize, blockSize: numericSize }
      : tokenSize
      ? {
          inlineSize: `var(--avatar-${tokenSize})`,
          blockSize: `var(--avatar-${tokenSize})`,
        }
      : {}),
    borderRadius: '9999px',
    background: 'var(--surface-2)',
    border: '1px solid var(--surface-3)',
    color: 'var(--text-strong, var(--text))',
    fontSize: counterFontSize,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...(typeof max === 'object' && max.style ? max.style : {}),
  };

  const overlapValue = typeof overlap === 'number' ? overlap : parseFloat(overlap) || 0;
  const counterAriaLabel = typeof counterLabel === 'function' ? counterLabel(extra) : counterLabel;

  return (
    <div className={clsx('ds-avatar-group', className)} style={style} {...rest}>
      {shown.map((child, index) => {
        if (!React.isValidElement(child)) {
          return null;
        }
        const childStyle = {
          ...(child.props.style || {}),
          zIndex: shown.length - index,
        };
        const mappedChild = React.cloneElement(child, {
          size: child.props.size ?? size,
          shape: child.props.shape ?? shape,
          style: childStyle,
        });
        return (
          <span
            key={child.key ?? index}
            className="ds-avatar-group__item"
            style={{ marginInlineStart: index === 0 ? 0 : -Math.abs(overlapValue) }}
          >
            {mappedChild}
          </span>
        );
      })}
      {extra > 0 && (
        <span
          className="ds-avatar-group__item"
          style={{ marginInlineStart: shown.length ? -Math.abs(overlapValue) : 0 }}
        >
          <span className="ds-avatar-group__counter" style={counterStyle} aria-label={counterAriaLabel}>
            +{extra}
          </span>
        </span>
      )}
    </div>
  );
};

const Avatar = Object.assign(AvatarComponent, { Group: AvatarGroup });

export { Avatar, AvatarGroup };
